import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight, Clock, Heart, Shield, Star, Check, Pen, Package,
  MessageCircle
} from "lucide-react";
import PageCTA from "@/components/PageCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay }} className={className}>
      {children}
    </motion.div>
  );
};

/* ════════════════════════════════════════════
   1.  HERO  —  min 700 px, image right, text left
   ════════════════════════════════════════════ */
const Hero = () => (
  <section className="relative bg-[var(--color-black)] overflow-hidden" data-testid="hero-section" style={{ minHeight: 'clamp(520px, 85vh, 700px)' }}>
    {/* BG image — full width with gradient */}
    <div className="absolute inset-0">
      <img src="/images/portfolio/branding-set.png" alt="" className="absolute right-0 top-0 w-full lg:w-[55%] h-full object-cover object-right-top" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-black)] via-[var(--color-black)]/85 lg:via-[var(--color-black)]/70 to-[var(--color-black)]/10 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-black)]/70 via-transparent to-[var(--color-black)]/30 pointer-events-none lg:hidden" />
    </div>

    <div className="container relative z-10 flex flex-col justify-center" style={{ minHeight: 'clamp(520px, 85vh, 700px)', paddingTop: 120, paddingBottom: 40 }}>
      <p className="eyebrow mb-5">Dla hodowców, klubów i trenerów</p>

      <h1 className="font-heading font-medium text-[var(--color-text-light)] leading-[1.02] mb-7" style={{ fontSize: 'clamp(42px, 5.5vw, 64px)' }}>
        PROFESJONALNY<br />
        <span className="heading-accent">WIZERUNEK</span><br />
        TWOJEJ HODOWLI
      </h1>

      <p className="font-body text-[var(--color-text-light-muted)] max-w-md mb-10" style={{ fontSize: 17, lineHeight: 1.7 }}>
        Projektujemy strony internetowe, grafiki i mediowe materiały tak, aby wzmacniały Twoją markę i przyciągały właściwych klientów.
      </p>

      <div className="flex flex-wrap gap-4 mb-0">
        <Link to="/kontakt"><button className="button-primary" data-testid="hero-cta">Zobacz ofertę <ArrowRight className="w-4 h-4" /></button></Link>
        <Link to="/realizacje"><button className="button-outline" data-testid="hero-portfolio">Portfolio</button></Link>
      </div>

      {/* Tags row — bottom */}
      <div className="flex flex-wrap gap-6 lg:gap-10 mt-auto pt-10 border-t border-[var(--color-border-dark)]">
        {[
          { icon: Star, t: "Profesjonalne" },
          { icon: Clock, t: "Na czas" },
          { icon: Heart, t: "Z pasją" },
          { icon: Shield, t: "Dla hodowców" },
        ].map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <v.icon className="w-4 h-4 text-[var(--color-gold)]" strokeWidth={1.4} />
            <span className="text-[11px] font-body font-medium uppercase tracking-widest text-[var(--color-text-light-muted)]">{v.t}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ════════════════════════════════════════════
   2.  USŁUGI  —  6 kart, min‑height 320 px
   ════════════════════════════════════════════ */
const services = [
  { icon: "/icons/icon-strony-www.svg", title: "Strony WWW", desc: "Nowoczesne strony internetowe dopasowane do Twojej hodowli.", href: "/strony-www" },
  { icon: "/icons/icon-grafiki.svg", title: "Grafiki", desc: "Grafiki reklamowe i wizerunkowe, które przyciągają uwagę.", href: "/grafiki-reklamowe" },
  { icon: "/icons/icon-logo-branding.svg", title: "Logo i branding", desc: "Logo, identyfikacja wizualna i materiały budujące markę.", href: "/logo-branding" },
  { icon: "/icons/icon-gadzety.svg", title: "Gadżety", desc: "Koszulki, kubki i inne produkty z Twoim projektem.", href: "/odziez-z-nadrukiem" },
  { icon: "/icons/icon-gazetki.svg", title: "Gazetki", desc: "Gazetki, ulotki, ogłoszenia dla wydarzeń i sprzedaży.", href: "/grafiki-reklamowe" },
  { icon: "/icons/icon-druk-reklama.svg", title: "Druk i reklama", desc: "Wizytówki, banery, plakaty i inne materiały drukowane.", href: "/grafiki-reklamowe" },
];

const Services = () => (
  <section className="bg-[var(--color-cream)]" style={{ paddingBlock: 96 }} data-testid="services-section">
    <div className="container">
      <div className="section-divider mb-14" />
      <FadeIn className="text-center mb-14">
        <h2 className="font-heading text-[var(--color-text-dark)]" style={{ fontSize: 'clamp(32px, 3.5vw, 42px)' }}>
          CO MOŻEMY DLA CIEBIE STWORZYĆ?
        </h2>
      </FadeIn>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5">
        {services.map((s, i) => (
          <FadeIn key={i} delay={i * 0.05}>
            <Link to={s.href}>
              <div className="service-card group flex flex-col items-center" style={{ minHeight: 320 }} data-testid={`service-card-${i}`}>
                <img src={s.icon} alt="" className="w-14 h-14 mb-7" />
                <h3 className="font-heading text-lg font-medium text-[var(--color-text-dark)] mb-4 uppercase tracking-wide text-center">{s.title}</h3>
                <p className="text-[13px] font-body text-[var(--color-text-muted)] leading-relaxed text-center flex-1">{s.desc}</p>
                <span className="text-xs font-body font-semibold uppercase tracking-wider text-[var(--color-gold)] inline-flex items-center gap-1 mt-6">
                  Zobacz <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ════════════════════════════════════════════
   3.  BESTSELLERY  —  6 produktów, jasne tło
   ════════════════════════════════════════════ */
const products = [
  { name: "Koszulka z logo", sub: "Damska", price: "od … zł", img: "/images/products/tshirt.png" },
  { name: "Kubek z logo", sub: "Czarny", price: "od … zł", img: "/images/products/mug.png" },
  { name: "Torba bawełniana", sub: "Eko", price: "od … zł", img: "/images/products/bag.png" },
  { name: "Plakat z psem A2", sub: "Premium", price: "od … zł", img: "/images/products/poster.png" },
  { name: "Teczka na dokumenty", sub: "Dla psa", price: "od … zł", img: "/images/portfolio/book.png" },
  { name: "Kalendarz ścienny", sub: "A3 psa", price: "od … zł", img: "/images/portfolio/banner.png" },
];

const Bestsellers = () => (
  <section className="bg-[var(--color-cream)]" style={{ paddingBlock: 80 }} data-testid="bestsellers-section">
    <div className="container">
      <div className="flex items-center justify-between mb-10">
        <FadeIn><h2 className="font-heading text-[var(--color-text-dark)] uppercase" style={{ fontSize: 'clamp(28px, 3vw, 38px)' }}>Bestsellery</h2></FadeIn>
        <FadeIn>
          <Link to="/realizacje" className="text-xs font-body font-semibold uppercase tracking-wider text-[var(--color-gold)] flex items-center gap-2 hover:text-[var(--color-gold-light)] transition-colors">
            Zobacz całą ofertę <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5">
        {products.map((p, i) => (
          <FadeIn key={i} delay={i * 0.04}>
            <div className="product-card" data-testid={`bestseller-${i}`}>
              <div className="aspect-[4/5] bg-[var(--color-cream-dark)]/30 flex items-center justify-center p-4 overflow-hidden">
                <img src={p.img} alt={p.name} className="w-full h-full object-contain" />
              </div>
              <div className="p-4">
                <h3 className="font-body text-xs font-bold uppercase tracking-wider text-[var(--color-text-dark)]">{p.name}</h3>
                <p className="text-[11px] font-body text-[var(--color-text-muted)] mt-0.5">{p.sub}</p>
                <p className="font-heading text-xl font-medium text-[var(--color-text-dark)] mt-2">{p.price}</p>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ════════════════════════════════════════════
   4.  CENNIK + GRAFIKI  —  side-by-side
   ════════════════════════════════════════════ */
const pricingCards = [
  { name: "Start", sub: "Strona na dobry początek", price: "od 1 900 zł", hi: false, list: ["Do 5 podstron", "Indywidualny projekt", "Wersja mobilna", "Galeria zdjęć", "Formularz kontaktowy", "Podstawowe SEO"] },
  { name: "Hodowla", sub: "Kompletna strona hodowli", price: "od 2 900 zł", hi: true, list: ["Do 10 podstron", "Profile psów i mioty", "Galerie zdjęć", "Blog / Aktualności", "Panel do edycji", "SEO + social media"] },
  { name: "Premium", sub: "Rozbudowana strona premium", price: "od 4 900 zł", hi: false, list: ["Do 15 podstron", "Projekt premium", "Rodowody i osiągnięcia", "Galerie foto + video", "Rozszerzone SEO", "Priorytetowe wsparcie"] },
];

const graphicsGrid = [
  { t: "Zapowiedź miotu", p: "od … zł", img: "/images/hodowlane/01_zapowiedz_miotu.webp" },
  { t: "Social media", p: "od … zł", img: "/images/hodowlane/02_social_media.webp" },
  { t: "Ogłoszenia i posty", p: "od … zł", img: "/images/hodowlane/03_ogloszenia_i_posty.webp" },
  { t: "Wizytówki", p: "od … zł", img: "/images/hodowlane/04_wizytowki.webp" },
  { t: "Plakaty i banery", p: "od … zł", img: "/images/hodowlane/05_plakaty_i_banery.webp" },
  { t: "Obróbka zdjęć", p: "od … zł", img: "/images/hodowlane/06_obrobka_zdjec.webp" },
];

const PricingGraphics = () => (
  <section className="bg-[var(--color-cream)]" style={{ paddingBlock: 80 }} data-testid="pricing-section">
    <div className="container">
      <div className="grid lg:grid-cols-2 gap-16">
        {/* Left — pricing */}
        <div>
          <FadeIn>
            <h2 className="font-heading text-[var(--color-text-dark)] uppercase mb-1" style={{ fontSize: 'clamp(26px, 2.5vw, 34px)' }}>Strony internetowe</h2>
            <p className="text-sm font-body text-[var(--color-text-muted)] mb-8">Wybierz pakiet dopasowany do Twoich potrzeb.</p>
          </FadeIn>
          <div className="grid grid-cols-3 gap-4">
            {pricingCards.map((c, i) => (
              <FadeIn key={i} delay={i * 0.06}>
                <div className={`pricing-card flex flex-col ${c.hi ? 'pricing-card--featured' : ''}`} data-testid={`pricing-${i}`}>
                  {c.hi && <span className="pricing-badge mb-3">Najczęściej wybierany</span>}
                  <p className="font-body text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-dark)]">{c.name}</p>
                  <p className="text-[10px] font-body text-[var(--color-text-muted)] mt-0.5 uppercase tracking-wider">{c.sub}</p>
                  <div className="pricing-price mt-2 mb-4" style={{ fontSize: 36 }}>{c.price}</div>
                  <ul className="space-y-1.5 flex-1">
                    {c.list.map((f, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <Check className="w-3 h-3 text-[var(--color-gold)] mt-0.5 flex-shrink-0" />
                        <span className="text-[11px] font-body text-[var(--color-text-muted)]">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/kontakt" className="mt-5">
                    <button className={`w-full justify-center text-[11px] ${c.hi ? 'button-primary' : 'button-outline text-[var(--color-text-dark)]'}`} style={{ minHeight: 42, padding: '0 16px' }}>
                      Zobacz
                    </button>
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <Link to="/pakiety" className="inline-flex items-center gap-1 text-xs font-body font-semibold uppercase tracking-wider text-[var(--color-gold)] mt-6 hover:text-[var(--color-gold-light)] transition-colors">
              Zobacz wszystkie pakiety <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeIn>
        </div>

        {/* Right — graphics */}
        <div>
          <FadeIn>
            <h2 className="font-heading text-[var(--color-text-dark)] uppercase mb-1" style={{ fontSize: 'clamp(26px, 2.5vw, 34px)' }}>Grafiki hodowlane</h2>
            <p className="text-sm font-body text-[var(--color-text-muted)] mb-8">Zapewniamy unikalne, przemyślane projekty takie jak:</p>
          </FadeIn>
          <div className="grid grid-cols-3 gap-4">
            {graphicsGrid.map((g, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <div className="product-card overflow-hidden group">
                  <div className="aspect-[4/3] bg-[var(--color-cream-dark)]/30 overflow-hidden">
                    <img src={g.img} alt={g.t} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <h4 className="text-[10px] font-body font-bold uppercase tracking-wider text-[var(--color-text-dark)] leading-tight">{g.t}</h4>
                    <p className="font-heading text-base text-[var(--color-text-dark)] font-medium mt-1">{g.p}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <Link to="/grafiki-reklamowe" className="inline-flex items-center gap-1 text-xs font-body font-semibold uppercase tracking-wider text-[var(--color-gold)] mt-6 hover:text-[var(--color-gold-light)] transition-colors">
              Zobacz wszystkie grafiki <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeIn>
        </div>
      </div>
    </div>
  </section>
);

/* ════════════════════════════════════════════
   5.  DLACZEGO APISDESIGN  —  dark, 5 items
   ════════════════════════════════════════════ */
const whyUs = [
  { icon: Heart, t: "Doświadczenie i pasja", d: "Od lat współpracuję z hodowcami i klubami kynologicznymi." },
  { icon: MessageCircle, t: "Doradztwo", d: "Pomagam wybrać najlepsze rozwiązania dopasowane do Twoich celów." },
  { icon: Shield, t: "Bezpieczeństwo danych", d: "Twoje dane i materiały są u mnie w pełni bezpieczne." },
  { icon: Clock, t: "Na czas", d: "Dotrzymuję terminów i zawsze informuję o postępach." },
  { icon: Star, t: "Wysoka jakość", d: "Każdy projekt tworzę z dbałością o detale i estetykę wykonania." },
];

const WhyUs = () => (
  <section className="bg-[var(--color-charcoal)]" style={{ paddingBlock: 96 }} data-testid="why-us-section">
    <div className="container">
      <div className="section-divider mb-14" />
      <FadeIn className="text-center mb-14">
        <h2 className="font-heading text-[var(--color-text-light)]" style={{ fontSize: 'clamp(28px, 3vw, 38px)' }}>DLACZEGO APISDESIGN?</h2>
      </FadeIn>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
        {whyUs.map((v, i) => (
          <FadeIn key={i} delay={i * 0.05}>
            <div className="text-center">
              <v.icon className="w-9 h-9 text-[var(--color-gold)] mx-auto mb-5" strokeWidth={1.2} />
              <h3 className="font-body text-xs font-bold uppercase tracking-wider text-[var(--color-text-light)] mb-3">{v.t}</h3>
              <p className="text-[13px] font-body text-[var(--color-text-light-muted)] leading-relaxed">{v.d}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ════════════════════════════════════════════
   6.  BLOG  —  4 karty, cream bg
   ════════════════════════════════════════════ */
const BlogPreview = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { axios.get(`${API}/blog`).then(r => setPosts(r.data.slice(0, 4))); }, []);
  const fmt = d => new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <section className="bg-[var(--color-cream)]" style={{ paddingBlock: 96 }} data-testid="blog-section">
      <div className="container">
        <div className="flex items-center justify-between mb-10">
          <FadeIn><h2 className="font-heading text-[var(--color-text-dark)] uppercase" style={{ fontSize: 'clamp(28px, 3vw, 38px)' }}>Najnowsze na blogu</h2></FadeIn>
          <FadeIn>
            <Link to="/blog" className="text-xs font-body font-semibold uppercase tracking-wider text-[var(--color-gold)] flex items-center gap-2 hover:text-[var(--color-gold-light)] transition-colors">
              Zobacz wszystkie artykuły <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeIn>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {posts.map((p, i) => (
            <FadeIn key={p.id} delay={i * 0.05}>
              <Link to={`/blog/${p.slug}`} className="group" data-testid={`blog-preview-${i}`}>
                <div className="overflow-hidden rounded">
                  <div className="aspect-[4/3] overflow-hidden rounded bg-[var(--color-cream-dark)]/50">
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="pt-4">
                    <p className="text-[11px] font-body text-[var(--color-text-muted)]">{fmt(p.created_at)}</p>
                    <h3 className="font-heading text-lg font-medium text-[var(--color-text-dark)] group-hover:text-[var(--color-gold)] transition-colors leading-snug mt-2 line-clamp-2">
                      {p.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-xs font-body font-semibold text-[var(--color-gold)] mt-3">
                      czytaj dalej <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════════
   7.  CTA  —  dark, centered, WhatsApp
   ════════════════════════════════════════════ */
const CTA = () => (
  <PageCTA />
);

/* ════════════════════════════════════════════
   HOME
   ════════════════════════════════════════════ */
const Home = () => {
  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
    axios.post(`${API}/admin/setup`).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <Services />
      <Bestsellers />
      <PricingGraphics />
      <WhyUs />
      <BlogPreview />
      <CTA />
    </div>
  );
};

export default Home;
